import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import NavBar from '../NavBar';
import LoginPage from '../../pages/LoginPage';
import { setLangPref } from '../../App';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  clearStoredAuthSession,
  getCurrentUser,
  logoutUser,
  persistAuthSession,
  readStoredAuthSession,
} from '../../services/userService';

const AppLayout = () => {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const isAdmin = currentUser?.role === 'ADMIN';

  useEffect(() => {
    if (currentUser && currentUser.role !== 'ADMIN' && lang !== 'he') {
      setLangPref('he');
      navigate('/heb', { replace: true });
    }
  }, [currentUser, lang, navigate]);

  useEffect(() => {
    if (!currentUser && lang !== 'he') {
      setLangPref('he');
      navigate('/heb', { replace: true });
    }
  }, [currentUser, lang, navigate]);

  // Apply RTL/LTR direction and language to the document root
  useEffect(() => {
    document.documentElement.setAttribute('lang', lang === 'he' ? 'he' : 'en');
    document.documentElement.setAttribute('dir', lang === 'he' ? 'rtl' : 'ltr');
    return () => {
      document.documentElement.setAttribute('lang', 'en');
      document.documentElement.setAttribute('dir', 'ltr');
    };
  }, [lang]);

  // Restore auth session on mount
  useEffect(() => {
    let isActive = true;

    const restoreSession = async () => {
      const storedSession = readStoredAuthSession();
      const token = storedSession?.session?.token;

      if (!token) {
        return;
      }

      try {
        const payload = await getCurrentUser(token);

        if (!isActive) {
          return;
        }

        const hydratedAuthPayload = {
          data: payload.data,
          session: {
            ...(storedSession?.session ?? {}),
            ...(payload.session ?? {}),
            token,
          },
        };

        setCurrentUser(payload.data ?? null);
        persistAuthSession(hydratedAuthPayload);
      } catch (error) {
        if (!isActive) {
          return;
        }

        clearStoredAuthSession();
        setCurrentUser(null);
      }
    };

    restoreSession();

    return () => {
      isActive = false;
    };
  }, []);

  const handleAccountClick = () => {
    if (!currentUser) {
      setIsLoginModalOpen(true);
    }
  };

  const handleCloseLoginModal = () => {
    setIsLoginModalOpen(false);
  };

  const handleAuthSuccess = (authPayload) => {
    setCurrentUser(authPayload?.data ?? null);
    persistAuthSession(authPayload);
    setIsLoginModalOpen(false);
  };

  const handleLogout = async () => {
    const storedSession = readStoredAuthSession();
    const token = storedSession?.session?.token;

    try {
      await logoutUser(token);
    } catch (error) {
      // Ignore logout API failures locally and still clear the session on the client.
    } finally {
      clearStoredAuthSession();
      setCurrentUser(null);
      setIsLoginModalOpen(false);
    }
  };

  const handleSwitchLanguage = () => {
    const next = lang === 'he' ? 'en' : 'he';
    setLangPref(next);
    navigate(next === 'he' ? '/heb' : '/', { replace: true });
  };

  const showToast = (message) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToast(message);
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  };

  const handleSubmitNavigate = () => {
    if (currentUser) {
      navigate(lang === 'he' ? '/heb/submit' : '/submit');
    } else {
      showToast(t.submit.loginRequired);
    }
  };

  const handlePreferencesNavigate = () => {
    navigate(lang === 'he' ? '/heb/preferences' : '/preferences');
  };

  const handleAuthRequiredMessage = (message) => {
    showToast(message);
  };

  const primaryNavLinks = [
    { label: t.nav.home, to: lang === 'he' ? '/heb' : '/' },
    { label: t.nav.explorer, to: lang === 'he' ? '/heb/explorer' : '/explorer' },
    { label: t.nav.favorites, to: lang === 'he' ? '/heb/favorites' : '/favorites' },
    { label: t.nav.articles, to: lang === 'he' ? '/heb/articles' : '/articles' },
    {
      label: t.diary?.navLabel || 'Food Diary',
      to: lang === 'he' ? '/heb/diary' : '/diary',
      disabled: !currentUser,
    },
  ];

  return (
    <>
      <NavBar
        brand={t.nav.brand}
        navLinks={primaryNavLinks}
        searchPlaceholder={t.nav.search}
        onAccountClick={handleAccountClick}
        onLogout={handleLogout}
        onPreferencesClick={currentUser ? handlePreferencesNavigate : null}
        preferencesLabel={t.preferences?.navLabel || 'My Preferences'}
        currentUser={currentUser}
        onSwitchLanguage={handleSwitchLanguage}
        switchLangLabel={t.nav.switchLang}
        showLanguageSwitch={isAdmin}
      />
      <Outlet
        context={{
          currentUser,
          setCurrentUser,
          onSubmitNavigate: handleSubmitNavigate,
          onAuthRequiredMessage: handleAuthRequiredMessage,
        }}
      />
      {isLoginModalOpen ? (
        <LoginPage isModal onClose={handleCloseLoginModal} onAuthSuccess={handleAuthSuccess} />
      ) : null}
      {toast ? (
        <div className="auth-toast" role="alert" aria-live="assertive">
          {toast}
        </div>
      ) : null}
    </>
  );
};

export default AppLayout;
